/**
 * VerifyEmailView — landing for the magic-link sent by auth-api after
 * email signup (Phase 2 of public-SaaS rollout).
 *
 * Flow:
 *   1. User clicks `<instance-origin>/verify-email?token=…`
 *      from their inbox.
 *   2. We POST → GET /api/v1/auth/verify-email?token=…&next=… which
 *      flips email_verified=true server-side and returns the original
 *      `next_url` the signup remembered (defaults to "/").
 *   3. We surface success / already-verified / error states and
 *      auto-redirect on success.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { verifyEmail } from '../auth'
import type { VerifyEmailResponse } from '../auth'
import { REDIRECT_ALLOWED_ORIGINS } from '../config'
import { resolveRedirect } from '../lib/safeRedirect'

export function VerifyEmailView() {
  const [params] = useSearchParams()
  const pathParams = useParams<{ tokenPath?: string }>()
  const navigate = useNavigate()
  // Support both /verify-email?token=<jwt> (query) and /verify/<jwt> (path)
  const token = params.get('token') || pathParams.tokenPath || null
  const nextOverride = params.get('next') || undefined

  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; r: VerifyEmailResponse }
    | { kind: 'already'; r: VerifyEmailResponse }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' })

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: 'Kein Verifizierungs-Token in der URL.' })
      return
    }
    void (async () => {
      try {
        const r = await verifyEmail(token, nextOverride)
        if (r.already_verified) {
          setState({ kind: 'already', r })
        } else if (r.verified) {
          setState({ kind: 'ok', r })
        } else {
          setState({
            kind: 'error',
            message: 'Verifizierung fehlgeschlagen — Token ungültig oder abgelaufen.',
          })
          return
        }
        // Post #4963: next_url may be an ABSOLUTE external URL (3D Presenter).
        // The router must never see one — it would rewrite it into a bogus
        // SPA path (`/verify/<token>/https:/presenter…`, blank page). External
        // targets go through a full navigation, but only for allowlisted
        // origins; internal paths keep using the router; anything else → "/".
        const decision = resolveRedirect(
          r.next_url,
          REDIRECT_ALLOWED_ORIGINS,
          typeof window !== 'undefined' ? window.location.origin : null,
        )
        if (decision.kind === 'fallback' && r.next_url) {
          console.warn('[verify-email] next_url rejected, falling back to "/":', decision.reason)
        }
        window.setTimeout(() => {
          if (decision.kind === 'external') {
            window.location.replace(decision.url)
          } else {
            navigate(decision.kind === 'internal' ? decision.path : '/', { replace: true })
          }
        }, 1500)
      } catch (e) {
        setState({ kind: 'error', message: (e as Error).message || String(e) })
      }
    })()
  }, [token, nextOverride, navigate])

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          padding: 28,
          borderRadius: 12,
          background: 'var(--surface, #fff)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <h1 style={{ marginTop: 0, fontSize: 22 }}>Email-Bestätigung</h1>

        {state.kind === 'loading' && (
          <p style={{ color: 'var(--text-muted, #666)' }}>Prüfe Token …</p>
        )}

        {state.kind === 'ok' && (
          <div
            style={{
              padding: 12,
              background: '#d4edda',
              color: '#155724',
              borderRadius: 6,
            }}
          >
            <strong>Bestätigt.</strong> Du wirst weitergeleitet …
          </div>
        )}

        {state.kind === 'already' && (
          <div
            style={{
              padding: 12,
              background: '#fff3cd',
              color: '#856404',
              borderRadius: 6,
            }}
          >
            <strong>Email war bereits bestätigt.</strong> Du wirst weitergeleitet …
          </div>
        )}

        {state.kind === 'error' && (
          <div
            style={{
              padding: 12,
              background: '#fee',
              color: '#900',
              borderRadius: 6,
            }}
          >
            {state.message}
          </div>
        )}
      </div>
    </div>
  )
}
